import { useMemo, useState, type ChangeEvent, type FormEvent, type ReactElement } from 'react';
import type {
  FleetAircraftView,
  AircraftType,
  MaintenanceCheckType,
  NewMaintenanceEvent,
} from '../../../domain/models/contracts';
import {
  CHECK_RULES,
  MINUTES_PER_DAY,
  computeCheckStatuses,
  formatDuration,
  formatMinuteOfDay,
  formatSimTime,
  hasErrors,
  legsForAircraft,
  locationAt,
  mod,
  parseTimeInput,
  validateNewMaintenance,
  type ScheduleContext,
} from '../../../domain/scheduling/scheduleMath';
import { IssueList } from './IssueList';
import { ModalShell } from './ModalShell';

interface ScheduleMaintenanceModalProps {
  aircraft: FleetAircraftView;
  type: AircraftType;
  context: ScheduleContext;
  initialStartAbsMinute: number;
  onSubmit: (event: NewMaintenanceEvent) => void;
  onClose: () => void;
}

const CHECK_ORDER: readonly MaintenanceCheckType[] = ['A_CHECK', 'C_CHECK', 'D_CHECK'];

export function ScheduleMaintenanceModal({
  aircraft,
  type,
  context,
  initialStartAbsMinute,
  onSubmit,
  onClose,
}: ScheduleMaintenanceModalProps): ReactElement {
  const [check, setCheck] = useState<MaintenanceCheckType>('A_CHECK');
  const [startAbs, setStartAbs] = useState<number>(initialStartAbsMinute);

  const { schedule, nowAbsMinute } = context;
  const rule = CHECK_RULES[check];
  const startDay = Math.floor(startAbs / MINUTES_PER_DAY);
  const startMinute = mod(startAbs, MINUTES_PER_DAY);
  const minDay = Math.floor(nowAbsMinute / MINUTES_PER_DAY);

  const legs = useMemo(
    () => legsForAircraft(schedule, aircraft.registration),
    [schedule, aircraft.registration],
  );
  const aircraftEvents = useMemo(
    () => schedule.maintenance.filter((event) => event.aircraftReg === aircraft.registration),
    [schedule, aircraft.registration],
  );

  // The aircraft is checked wherever it is sitting when the window opens.
  const airportId = locationAt(aircraft, legs, startMinute);
  const airport = context.airports[airportId];

  const { draft, issues } = useMemo(() => {
    const next: NewMaintenanceEvent = {
      aircraftReg: aircraft.registration,
      check,
      airportId,
      startAbsMinute: startAbs,
      durationMinutes: rule.downtimeMinutes,
    };
    return { draft: next, issues: validateNewMaintenance(context, next) };
  }, [context, aircraft.registration, check, airportId, startAbs, rule.downtimeMinutes]);

  const statuses = useMemo(
    () => computeCheckStatuses(aircraft, legs, aircraftEvents, nowAbsMinute),
    [aircraft, legs, aircraftEvents, nowAbsMinute],
  );

  const onDayChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const day = Number(event.target.value);
    if (Number.isInteger(day)) setStartAbs(day * MINUTES_PER_DAY + startMinute);
  };

  const onTimeChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const parsed = parseTimeInput(event.target.value);
    if (parsed !== null) setStartAbs(startDay * MINUTES_PER_DAY + parsed);
  };

  const canSubmit = !hasErrors(issues);

  const onFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (canSubmit) onSubmit(draft);
  };

  return (
    <ModalShell
      title="Schedule Maintenance"
      subtitle={`${aircraft.registration} · ${type.id}`}
      onClose={onClose}
    >
      <form className="fsg-modal-body" onSubmit={onFormSubmit}>
        <table className="fsg-checks">
          <thead>
            <tr>
              <th>Check</th>
              <th>Flown since</th>
              <th>Remaining</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {statuses.map((status) => (
              <tr key={status.check}>
                <td>{CHECK_RULES[status.check].label}</td>
                <td>
                  {status.hoursSinceCheck.toFixed(0)} / {status.intervalHours.toLocaleString()} h
                </td>
                <td>{status.hoursRemaining.toFixed(0)} h</td>
                <td>
                  <span className={`fsg-badge fsg-badge-${status.severity.toLowerCase()}`}>
                    {status.severity === 'SCHEDULED' && status.scheduledStartAbsMinute !== null
                      ? `Scheduled ${formatSimTime(status.scheduledStartAbsMinute)}`
                      : status.severity.charAt(0) + status.severity.slice(1).toLowerCase()}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <label className="fsg-field">
          <span className="fsg-field-label">Check type</span>
          <select
            className="fsg-input"
            value={check}
            onChange={(event) => {
              const next = CHECK_ORDER.find((option) => option === event.target.value);
              if (next !== undefined) setCheck(next);
            }}
          >
            {CHECK_ORDER.map((option) => (
              <option key={option} value={option}>
                {CHECK_RULES[option].label} · {formatDuration(CHECK_RULES[option].downtimeMinutes)}
                {CHECK_RULES[option].requiresMaintenanceBase ? ' · heavy base' : ''}
              </option>
            ))}
          </select>
        </label>

        <div className="fsg-field-row">
          <label className="fsg-field">
            <span className="fsg-field-label">Start day</span>
            <input
              className="fsg-input"
              type="number"
              min={minDay}
              step={1}
              value={startDay}
              onChange={onDayChange}
            />
          </label>
          <label className="fsg-field">
            <span className="fsg-field-label">Start time (sim UTC)</span>
            <input
              className="fsg-input"
              type="time"
              step={300}
              value={formatMinuteOfDay(startMinute)}
              onChange={onTimeChange}
            />
          </label>
        </div>

        <dl className="fsg-summary">
          <div>
            <dt>Location</dt>
            <dd>{airport === undefined ? airportId : `${airport.id} · ${airport.name}`}</dd>
          </div>
          <div>
            <dt>Downtime</dt>
            <dd>{formatDuration(rule.downtimeMinutes)}</dd>
          </div>
          <div>
            <dt>Back in service</dt>
            <dd>{formatSimTime(startAbs + rule.downtimeMinutes)}</dd>
          </div>
        </dl>

        <IssueList issues={issues} />

        <footer className="fsg-modal-foot">
          <button type="button" className="fsg-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="fsg-btn fsg-btn-primary" disabled={!canSubmit}>
            Schedule {rule.label}
          </button>
        </footer>
      </form>
    </ModalShell>
  );
}
